import { createFileRoute, Link, SearchSchemaInput, useNavigate } from '@tanstack/react-router'
import {
    Card,
} from "@/components/ui/card"
import {
    Copy,
    RefreshCcw,
    ExternalLink,
    TriangleAlert,
} from 'lucide-react';
import advancedFormat from 'dayjs/plugin/advancedFormat'
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Api, atDeliveryCancellationIdReasonMapping, cancellationIdReasonMapping,
    copyOrderDataToClipboard,
    copyTextToClipboard,
    getLspName,
    internalCancellationIdReasonMapping,
    is_shikhar_account
} from '@/lib/utils';
import { AccountInfo, Order } from '@/lib/interfaces';
import { useStore } from '@/store';
import { useShallow } from 'zustand/shallow';
import { Skeleton } from "@/components/ui/skeleton"
import { toast } from 'sonner';
import { useEffect, useState, ReactNode } from 'react';
import EventTimeLine from "@/components/features/event-timeline"
import { SearchOrderDialog } from '@/components/features/orders/search-order';
import OrderFulfillmentData from '@/components/features/orders/order-fulfillment-data';
import OrderSearchAttempts from '@/components/features/orders/order-search-attempts';
import OrderDeliveryDetails from '@/components/features/orders/order-delivery-details';
import Tooltip from '@/components/ui/tooltip';
import { OrderActions } from '@/components/features/orders/order-actions';
import { Button } from '@/components/ui/button';
import OrderIssuesList from '@/components/features/orders/order-issues-list';
import { orderGigTimeRange } from '@/components/features/orders/data-table';

dayjs.extend(advancedFormat)

const DisplayOrderAttributes = ({ fieldName, fieldValue }: { fieldName: string, fieldValue: string | undefined }) => {
    return <div>
        <p className='text-sm text-slate-500 font-medium'>{fieldName}</p>
        {fieldValue !== undefined ? <p className='font-medium text-sm ml'>{fieldValue || '--'}</p> : <Skeleton className="h-4 w-[250px]" />}
    </div>
}

export const Route = createFileRoute('/_auth/orders/$orderId')({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown> & SearchSchemaInput): { search: boolean } => {
        return {
            search: search.search as boolean || false
        }
    }
})

const cancellationInfo = (order: Order) => {
    return (order.cancellationReasonText || ({ ...cancellationIdReasonMapping, ...atDeliveryCancellationIdReasonMapping, ...internalCancellationIdReasonMapping }[order.cancellationReason] || '') || order.cancellationReason)
}

const getEventTimelines = (order: Order | undefined) => {
    let timelines: {
        name: string,
        timestamp: string,
        line3?: ReactNode,
        line4?: ReactNode,
        line5?: ReactNode,
        type?: 'red' | 'green' | 'orange',
        ack?: {
            received: boolean,
            timestamp?: string
        }
    }[] = []
    if (order) {
        const ackInfo = order.ack_info
        if (order.createdAt) {
            timelines.push({
                name: 'Order Created',
                timestamp: order.createdAt
            })
        }

        if (order.orderReadyAt) {
            timelines.push({
                name: 'Order Ready',
                timestamp: order.orderReadyAt
            })
        }

        if (order.assignedAt) {
            timelines.push({
                name: 'Agent Assigned',
                timestamp: order.assignedAt,
                ack: ackInfo?.assigned
            })
        }

        if (order.atpickupAt) {
            timelines.push({
                name: 'Reached Pickup',
                timestamp: order.atpickupAt,
                ack: ackInfo?.at_pickup
            })
        }

        if (order.pickedupAt) {
            timelines.push({
                name: 'Order Picked up',
                timestamp: order.pickedupAt,
                ack: ackInfo?.picked_up
            })
        }

        if (order.atdeliveryAt) {
            timelines.push({
                name: 'Reached Delivery',
                timestamp: order.atdeliveryAt,
                ack: ackInfo?.at_delivery
            })
        }

        if (order.deliveredAt) {
            timelines.push({
                name: 'Order Delivered',
                timestamp: order.deliveredAt,
                type: 'green',
                ack: ackInfo?.delivered
            })
        }

        if (order.cancelledAt && !order.orderState.includes('RTO')) {
            const stuckOrderIds = order.orderNote2?.stuck_route_order_ids
            const stuckRiderId = order.orderNote2?.stuck_rider_id ?? order.orderNote2?.rider_id
            const showStuckCard = Boolean(order.orderNote2?.stuck_route_id) || Boolean(stuckOrderIds?.length)
            timelines.push({
                name: 'Order Cancelled - by ' + order.cancelledBy,
                timestamp: order.cancelledAt,
                type: 'red',
                line3: cancellationInfo(order),
                line4: order.cancellingUser ? `Cancelling user: ${order.cancellingUser}` : undefined,
                line5: showStuckCard ? (
                    <span className='inline-flex flex-wrap items-center gap-1 text-amber-700'>
                        <TriangleAlert className='w-3.5 h-3.5 text-amber-600 shrink-0' />
                        Rider stuck{stuckRiderId ? <> (<span className='font-semibold text-amber-900'>{stuckRiderId}</span>)</> : null}
                        {stuckOrderIds?.length ? (
                            <>
                                {' '}on order{stuckOrderIds.length > 1 ? 's' : ''}:{' '}
                                {stuckOrderIds.map((id, idx) => (
                                    <span key={id}>
                                        <Link to="/orders/$orderId" params={{ orderId: String(id) }} target='_blank' rel='noopener noreferrer' className='text-blue-600 hover:underline'>
                                            {id}
                                        </Link>
                                        {idx < stuckOrderIds.length - 1 ? ', ' : ''}
                                    </span>
                                ))}
                            </>
                        ) : null}
                    </span>
                ) : undefined,
                ack: ackInfo?.cancelled
            })
        }

        if (order.rtoPickedupAt) {
            timelines.push({
                name: 'RTO Initiated - by ' + order.cancelledBy,
                timestamp: order.rtoPickedupAt,
                line3: cancellationInfo(order),
                line4: order.cancellingUser ? `Cancelled user: ${order.cancellingUser}` : undefined,
                type: 'orange',
                ack: ackInfo?.rto_initiated
            })
        }

        if (order.rtoDeliveredAt) {
            timelines.push({
                name: order.orderState === 'RTO-Disposed' ? 'RTO Disposed' : 'RTO Delivered',
                type: 'green',
                timestamp: order.rtoDeliveredAt,
                ack: ackInfo?.rto_delivered
            })
        }
    }

    return timelines.sort((a, b) => dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf())
}

const getCallbackHistoryTimeline = (order: Order | undefined) => {
    if (!order?.callback_history?.length) return []

    return [...order.callback_history]
        .map(c => ({
            name: `Callback ${c.successful ? 'Succeeded' : 'Failed'} - raised by ${c.raised_by}`,
            timestamp: c.timestamp,
            line3: !c.successful && c.error_code ? `Error code: ${c.error_code}` : undefined,
            type: (c.successful ? 'green' : 'red') as 'green' | 'red'
        }))
        .sort((a, b) => dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf())
}

function RouteComponent() {
    const { orderId } = Route.useParams()
    const queryClient = useQueryClient()
    const { search } = Route.useSearch()
    const [showSearchOrder, setSearchOrderDisplay] = useState(false)
    const [errMsg, setErrMsg] = useState<string | undefined>(undefined)

    const { token, accountId, email, unfulfillEnabled, role, setPage, loggedIn, isRetail } = useStore(useShallow(state => ({
        token: state.token,
        accountId: state.selectedAccount.id,
        email: state.email,
        unfulfillEnabled: state.selectedAccount.unfulfil_enabled,
        role: state.selectedAccount.role,
        setPage: state.setPage,
        loggedIn: state.loggedIn,
        isRetail: state.selectedAccount.is_retail
    })))

    const getOrderInfo = useQuery({
        queryKey: ['orderInfo', orderId],
        queryFn: () => Api<{ status: number, order: Order }>('/webui/order/info', "POST", { order: { id: orderId.replace(/\s+/g, '') } }, { token }),
        staleTime: 60 * 1000,
        refetchInterval: 90 * 1000,
        refetchOnWindowFocus: false,
        enabled: loggedIn && orderId !== '<orderId>',
        retry: 1
    })

    const getAccountUsers = useQuery({
        queryKey: ['getAccountUsers', accountId, token],
        queryFn: () => Api<AccountInfo>('/webui/get_account_info', 'POST', { account_id: accountId, mail_id: email || '' }, { token }),
        staleTime: Infinity,
        refetchInterval: Infinity,
        refetchOnWindowFocus: false,
        enabled: !!accountId && !!token
    })

    const order = getOrderInfo.data?.order
    const isShikhar = is_shikhar_account(accountId)

    useEffect(() => {
        setPage('Orders')
    }, [])

    useEffect(() => {
        if (search) {
            setSearchOrderDisplay(true)
        }
    }, [search])

    useEffect(() => {
        if (getOrderInfo.error) {
            setErrMsg('Error fetching order details.')
            setSearchOrderDisplay(true)
        }
    }, [getOrderInfo.error])

    const navigate = useNavigate({ from: Route.fullPath })

    return <div className='w-full lg:px-20 xl:px-52 px-5 py-2 *:mb-6'>
        <OrderActions
            order={order}
            orderId={order?.orderId?.replace(/\s+/g, '') || ''}
            showSearchOrder={() => setSearchOrderDisplay(true)}
            isRetail={isRetail}
            isSuperAdmin={/super_admin/.test(role)}
            token={token}
            accountId={accountId}
            email={email}
            unfulfillEnabled={unfulfillEnabled}
            onOrderReallocate={(_orderId, orderDetails) => {
                queryClient.setQueryData(['orderInfo', order?.orderId || ''], (oldData: { status: number, order: Order }) => {
                    return {
                        ...oldData,
                        order: {
                            ...oldData.order,
                            ...orderDetails
                        }
                    }
                })
            }} />
        <Card className='p-0'>
            <div>
                <div className='bg-gray-200 px-7 py-4 rounded-t-xl flex items-center gap-3'>
                    <p className='font-semibold text-lg'>Order Details</p>
                    {order ? <Tooltip title="Copy order details">
                        <Copy className="w-4 h-4 cursor-pointer active:opacity-50" onClick={async () => {
                            const didCopy = await copyOrderDataToClipboard(order)
                            if (didCopy) {
                                toast.success('Order details copied to clipboard')
                            } else {
                                toast.error('Failed to copy order details')
                            }
                        }} />
                    </Tooltip> : null}
                </div>
                <div className='py-3 px-4 grid gap-4 grid-cols-1 lg:px-10 lg:gap-6 lg:grid-cols-3 sm:grid-cols-2'>
                    <DisplayOrderAttributes fieldName='Order Id' fieldValue={order?.orderId} />
                    <DisplayOrderAttributes fieldName='Client Order Id' fieldValue={order?.clientOrderId} />
                    <DisplayOrderAttributes fieldName='Seller Order Id' fieldValue={order?.sellerOrderId || '-'} />
                    {order && (order.gigId || order.gig_id) ? (
                        <div>
                            <p className='text-sm text-slate-500 font-medium'>Gig</p>
                            <Link
                                className='font-medium text-sm text-blue-600 hover:underline flex items-center gap-1'
                                to='/gigs/$gigId'
                                params={{ gigId: String(order.gigId || order.gig_id) }}
                            >
                                {order.gigId || order.gig_id}
                                <ExternalLink className='w-3 h-3' />
                            </Link>
                        </div>
                    ) : null}
                    {order && (order.gigId || order.gig_id) && orderGigTimeRange(order) ? (
                        <DisplayOrderAttributes fieldName='Gig Timing' fieldValue={orderGigTimeRange(order)} />
                    ) : null}
                    <div>
                        <p className='text-sm text-slate-500 font-medium'>Map Distance</p>
                        {order === undefined ? <Skeleton className="h-4 w-[250px]" /> :
                            order.distance
                                ? <a
                                    className='font-medium text-sm text-blue-600 hover:underline flex items-center gap-1'
                                    href={`https://www.google.com/maps/dir/?api=1&origin=${order.pickupLatitude},${order.pickupLongitude}&destination=${order.dropLatitude},${order.dropLongitude}&travelmode=driving`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {order.distance.toFixed(2)} km
                                    <ExternalLink className='w-3 h-3' />
                                </a>
                                : <p className='font-medium text-sm'>--</p>
                        }
                    </div>
                    <DisplayOrderAttributes fieldName='Category' fieldValue={order?.orderCategory} />
                    <DisplayOrderAttributes fieldName='Order Amount' fieldValue={order ? `₹ ${order.orderAmount}` : undefined} />
                    <div>
                        <p className='text-sm text-slate-500 font-medium'>RateCard</p>
                        {order === undefined ? <Skeleton className="h-4 w-[250px]" /> :
                            <p className='font-medium text-sm'>
                                {order.ratecardId
                                    ? <>{order.ratecardId}{order.ratecardSurge ? <span className='text-slate-500'>{` (with ₹${order.ratecardSurge} surge)`}</span> : null}</>
                                    : '--'}
                            </p>
                        }
                    </div>
                </div>
                <div className='px-4 py-4 lg:px-10'>
                    <Button variant="blue" loading={getOrderInfo.isFetching} onClick={() => {
                        getOrderInfo.refetch()
                    }}>
                        <RefreshCcw className="w-4 h-4 cursor-pointer active:opacity-50" />
                        Refresh
                    </Button>
                </div>
            </div>
        </Card>
        {order ? <>
            <Card className='p-0'>
                <div>
                    <div className='bg-gray-200 px-7 py-4 rounded-t-xl'>
                        <p className='font-semibold text-lg'>LSP Details</p>
                    </div>
                    <div className='py-3 px-4 grid gap-4 grid-cols-1 lg:px-10 lg:gap-6 lg:grid-cols-3 sm:grid-cols-2'>
                        <DisplayOrderAttributes fieldName='LSP' fieldValue={getLspName(order.bppId || order['f.bppId'], order.providerId)} />
                        <DisplayOrderAttributes fieldName='Network Order Id' fieldValue={order.networkOrderId} />
                        <DisplayOrderAttributes fieldName='AWB Number' fieldValue={order.awbNumber} />
                        <DisplayOrderAttributes fieldName='LSP Distance' fieldValue={order['f.distance'] ? `${order['f.distance'].toFixed(2)} km` : '--'} />
                        <DisplayOrderAttributes fieldName='Rider Name' fieldValue={order.riderName} />
                        <DisplayOrderAttributes fieldName='Rider Number' fieldValue={order.riderNumber} />
                    </div>
                    <div className='px-4 py-4 lg:px-10'>
                        <Button variant="outline" disabled={!order.trackingUrl} onClick={() => order.trackingUrl ? window.open(order.trackingUrl, '_blank') : null}>
                            <ExternalLink className="w-4 h-4" />
                            Track
                        </Button>
                    </div>
                </div>
            </Card>
            <OrderDeliveryDetails order={order} />
            <Card className='p-0'>
                <div>
                    <div className='bg-gray-200 px-7 py-4 rounded-t-xl'>
                        <p className='font-semibold text-lg'>Timeline</p>
                    </div>
                    <div className='px-7'>
                        <EventTimeLine events={getEventTimelines(order)} showDurations />
                    </div>
                </div>
            </Card>
            {order.callback_history && order.callback_history.length > 0 ? <Card className='p-0'>
                <div>
                    <div className='bg-gray-200 px-7 py-4 rounded-t-xl'>
                        <p className='font-semibold text-lg'>Callback History</p>
                    </div>
                    <div className='px-7'>
                        <EventTimeLine events={getCallbackHistoryTimeline(order)} showDurations />
                    </div>
                </div>
            </Card> : null}
            <Card className='p-0'>
                <div>
                    <div className='bg-gray-200 px-7 py-4 rounded-t-xl'>
                        <p className='font-semibold text-lg'>Payment Details</p>
                    </div>
                    <div className='py-6 px-4 lg:px-32'>
                        <table className='w-full border-collapse [&_tr]:last:[&_td]:border-none  [&_td]:border-b [&_td]:p-2 [&_td]:last:text-right [&_td]:last:font-medium text-sm'>
                            <thead>
                                <tr>
                                    <td className='font-medium'>Description</td>
                                    <td className='font-medium'>Amount</td>
                                </tr>
                            </thead>
                            <tbody>
                                {order.codAmount ? (
                                    <tr>
                                        <td>COD Collection Amount</td>
                                        <td>{`₹ ${order.codAmount}`}</td>
                                    </tr>
                                ) : null}
                                <tr>
                                    <td>Delivery Fee</td>
                                    <td>{`₹ ${order.deliveryFee}`}</td>
                                </tr>
                                {order.orderNote2?.surge ? (
                                    <tr>
                                        <td className='pl-6 text-slate-500'>Surge</td>
                                        <td>{`₹ ${order.orderNote2.surge.client ?? 0} (client) + ₹ ${order.orderNote2.surge.weather ?? 0} (weather)`}</td>
                                    </tr>
                                ) : null}
                                <tr>
                                    <td>Platform Fee</td>
                                    <td>{`₹ ${order.platformFee}`}</td>
                                </tr>
                                <tr>
                                    <td>Price (with GST)</td>
                                    <td>{`₹ ${order.priceWithGST}`}</td>
                                </tr>
                                <tr>
                                    <td>Insured ?</td>
                                    <td>{`${order.insuranceFee > 0 ? "Yes" : "No"}`}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </Card>
            {isShikhar ? (
    <Card className='p-0'>
        <div>
            <div className='bg-gray-200 px-7 py-4 rounded-t-xl flex flex-col sm:flex-row sm:items-center sm:justify-between'>
                <div>
                    <p className='font-semibold text-lg'>Payment details</p>
                    <p className='text-xs text-slate-600 mt-1'>{`Order Id: ${order.orderId || '-'}`}</p>
                </div>
                <div className='mt-2 sm:mt-0 flex items-center gap-3 text-xs text-right text-slate-600'>
                    <div>
                        <p>{dayjs(order.createdAt).format('DD MMM YYYY')}</p>
                        <p className='mt-0.5'>{dayjs(order.createdAt).format('hh:mm A')}</p>
                    </div>
                    <Copy
                        className="w-4 h-4 cursor-pointer active:opacity-50"
                        onClick={async () => {
                            const lines = [
                                `Order Id: ${order.orderId || '-'}`,
                                `Date: ${dayjs(order.createdAt).format('DD MMM YYYY')}`,
                                `Time: ${dayjs(order.createdAt).format('hh:mm A')}`,
                                '',
                                `Pickup:`,
                                `${order.pickupName || '--'}`,
                                `${order.pickupPhone || '--'}`,
                                `${order.pickupAddress?.line1 || ''}`,
                                ...(order.pickupAddress?.line2 ? [order.pickupAddress.line2] : []),
                                `${order.pickupAddress?.city || ''}${order.pickupAddress?.city ? ',' : ''} ${order.pickupAddress?.state || ''}${order.pickupPincode ? ' - ' + order.pickupPincode : ''}`,
                                '',
                                `Drop:`,
                                `${order.dropName || '--'}`,
                                `${order.dropPhone || '--'}`,
                                `${order.dropAddress?.line1 || ''}`,
                                ...(order.dropAddress?.line2 ? [order.dropAddress.line2] : []),
                                `${order.dropAddress?.city || ''}${order.dropAddress?.city ? ',' : ''} ${order.dropAddress?.state || ''}${order.dropPincode ? ' - ' + order.dropPincode : ''}`,
                                '',
                                `Order Amount: ₹ ${order.orderAmount.toFixed?.(2) ?? order.orderAmount}`,
                                `Delivery Fee: ₹ ${order.deliveryFee.toFixed?.(2) ?? order.deliveryFee}`,
                                `Platform Fee: ₹ ${order.platformFee.toFixed?.(2) ?? order.platformFee}`,
                                `Total Fee With GST: ₹ ${order.priceWithGST.toFixed?.(2) ?? order.priceWithGST}`,
                            ]

                            const didCopy = await copyTextToClipboard(lines.join('\n'))
                            if (didCopy) {
                                toast.success('Payment details copied to clipboard')
                            } else {
                                toast.error('Failed to copy payment details')
                            }
                        }}
                    />
                </div>
            </div>

            <div className='py-5 px-6 lg:px-24 space-y-6'>
                <div className='grid gap-8 sm:grid-cols-2 text-sm'>
                    <div>
                        <p className='text-slate-500 font-medium text-xs mb-1'>Pickup</p>
                        <p className='font-semibold'>{order.pickupName || '--'}</p>
                        <p className='mt-1'>{order.pickupPhone || '--'}</p>
                        <div className='mt-2 text-xs text-slate-700 space-y-0.5'>
                            <p>{order.pickupAddress?.line1 || ''}</p>
                            {order.pickupAddress?.line2 && <p>{order.pickupAddress.line2}</p>}
                            <p>{`${order.pickupAddress?.city || ''}${order.pickupAddress?.city ? ',' : ''} ${order.pickupAddress?.state || ''}${order.pickupPincode ? ' - ' + order.pickupPincode : ''}`}</p>
                        </div>
                    </div>

                    <div>
                        <p className='text-slate-500 font-medium text-xs mb-1'>Drop</p>
                        <p className='font-semibold'>{order.dropName || '--'}</p>
                        <p className='mt-1'>{order.dropPhone || '--'}</p>
                        <div className='mt-2 text-xs text-slate-700 space-y-0.5'>
                            <p>{order.dropAddress?.line1 || ''}</p>
                            {order.dropAddress?.line2 && <p>{order.dropAddress.line2}</p>}
                            <p>{`${order.dropAddress?.city || ''}${order.dropAddress?.city ? ',' : ''} ${order.dropAddress?.state || ''}${order.dropPincode ? ' - ' + order.dropPincode : ''}`}</p>
                        </div>
                    </div>
                </div>

                <table className='w-full border-collapse text-sm mt-4'>
                    <tbody>
                        <tr className='border-b'>
                            <td className='py-2 pr-4 font-medium text-slate-800 text-left'>Order Amount</td>
                            <td className='py-2 pl-4 font-medium text-right'>
                                ₹ {order.orderAmount.toFixed?.(2) ?? order.orderAmount}
                            </td>
                        </tr>
                        <tr className='border-b'>
                            <td className='py-2 pr-4 font-medium text-slate-800 text-left'>Delivery Fee</td>
                            <td className='py-2 pl-4 font-medium text-right'>
                                ₹ {order.deliveryFee.toFixed?.(2) ?? order.deliveryFee}
                            </td>
                        </tr>
                        <tr className='border-b'>
                            <td className='py-2 pr-4 font-medium text-slate-800 text-left'>Platform Fee</td>
                            <td className='py-2 pl-4 font-medium text-right'>
                                ₹ {order.platformFee.toFixed?.(2) ?? order.platformFee}
                            </td>
                        </tr>
                        <tr>
                            <td className='py-2 pr-4 font-medium text-slate-800 text-left'>Total Fee With GST</td>
                            <td className='py-2 pl-4 font-medium text-right'>
                                ₹ {order.priceWithGST.toFixed?.(2) ?? order.priceWithGST}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </Card>
) : null}
            <OrderFulfillmentData orderFulfillment={order.fulfillmentList} orderId={order.orderId} role={role} orderAmount={order.orderAmount} deliveryFee={order.deliveryFee} orderState={order.orderState} networkOrderId={order.networkOrderId} />
            {order.searchList && order.searchList.length > 0 ? <OrderSearchAttempts searchAttempts={order.searchList} blacklistedLsps={getAccountUsers.data?.blacklisted_lsps || []} /> : null}
            {order.issueList.length > 0 ? <OrderIssuesList orderIssues={order.issueList} /> : null}
        </> : null}

        <SearchOrderDialog open={showSearchOrder} onClose={(resetSearch) => {
            setSearchOrderDisplay(false)
            if (resetSearch) {
                navigate({
                    search: () => ({
                        search: false
                    })
                })
            }
        }} errMsg={errMsg} clearErrMsg={() => setErrMsg(undefined)} searchedOrderId={orderId !== '<orderId>' ? orderId : undefined} />
    </div >
}
